import React from "react";
import { useResetPassword } from "./hooks/useResetPassword";
import { TextInput } from "@/components/form/TextInput";
import { validateResetPasswordForm } from "./validation";
import { InputLoader } from "@/components/ui/InputLoader";
import { assets } from "@/utils/assets";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/ui/LanguageSwitcher";

export const ResetPasswordPage = () => {
  const { t } = useTranslation();
  const wallpapers = assets.loginWallpapers;
  const [wallpaperIndex, setWallpaperIndex] = React.useState(0);
  const currentWallpaper = wallpapers[wallpaperIndex % wallpapers.length];

  React.useEffect(() => {
    if (wallpapers.length <= 1) return;
    const intervalId = setInterval(() => {
      setWallpaperIndex((prev) => (prev + 1) % wallpapers.length);
    }, 8000);
    return () => clearInterval(intervalId);
  }, [wallpapers.length]);

  const {
    formData,
    fieldsErrors,
    onSubmit,
    onChangeField,
    setFieldErrorsHandler,
    clearFieldErrorsHandler,
    isLoading,
    errorMessage,
  } = useResetPassword();

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    clearFieldErrorsHandler();
    const errors = validateResetPasswordForm(formData);

    if (Object.keys(errors).length > 0) {
      setFieldErrorsHandler(errors);
      return;
    }

    onSubmit(formData);
  };

  return (
    <div className="flex min-h-screen flex-col lg:h-full lg:flex-row">
      <div className="flex w-full flex-1 flex-col justify-center items-center bg-white px-6 py-10 sm:px-10 lg:w-1/2 lg:px-8 lg:py-0 relative">
        <div className="absolute top-6 right-6">
          <LanguageSwitcher />
        </div>
        <div className="mx-auto w-full max-w-md lg:w-1/2">
          <img
            src={assets.logo}
            alt="logo"
            className="mx-auto mb-8 h-20 lg:h-18 lg:mb-19"
          />

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="mb-6">
              <h1 className="text-3xl font-bold text-gray-900">
                {t("resetPassword.title")}
              </h1>
              <p className="mt-2 text-sm text-gray-600">
                {t("resetPassword.subtitle")}
              </p>
            </div>

            <TextInput
              label={t("resetPassword.passwordLabel")}
              name="password"
              id="password"
              type="password"
              placeholder={t("resetPassword.passwordPlaceholder")}
              value={formData.password}
              onChange={(e) => onChangeField(e, "password")}
              error={fieldsErrors.password}
            />

            <TextInput
              label={t("resetPassword.confirmPasswordLabel")}
              name="confirmPassword"
              id="confirmPassword"
              type="password"
              placeholder={t("resetPassword.confirmPasswordPlaceholder")}
              value={formData.confirmPassword}
              onChange={(e) => onChangeField(e, "confirmPassword")}
              error={fieldsErrors.confirmPassword}
            />

            {errorMessage && (
              <div className="text-red-500 text-sm">{errorMessage}</div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="mt-8 w-full rounded-full bg-primary px-4 py-3 text-base font-semibold text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-primary-300 lg:mt-10 lg:w-60 lg:h-15 lg:rounded-4xl lg:block lg:mx-auto"
            >
              {isLoading ? <InputLoader /> : t("resetPassword.continue")}
            </button>
          </form>
        </div>
      </div>

      <div className="hidden items-center justify-center lg:flex lg:w-1/2">
        <div className="relative h-full w-full">
          <img
            key={currentWallpaper}
            src={currentWallpaper}
            alt="reset password wallpaper"
            className="h-full w-full object-cover transition-opacity duration-700 ease-in-out"
          />
        </div>
      </div>
    </div>
  );
};
